"use client";

import { ProfileContext } from "@/context/useProfileProps";
import RecruiterViewContextWrapper from "@/context/useRecruiterView";
import { useMobile } from "@/hooks";
import { EventTrackingProps, ToastType } from "@/types/common";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import { Toast } from "@monsterindia/global-ui-components";
import { memo, useCallback, useMemo, useState } from "react";

import ExternalAd from "@/components/profile/externalAd/ExternalAd";
import ProfileScore from "@/components/profile/profileScore";

import translation from "../../utils/translation_profile.json";
import ActivityLevel from "./activityLevel";
import RecruiterActions from "./recruiterActions";
import SearchAppearances from "./searchAppearances";

const noScrollbarStyle = `
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const cleverTapEventTypes = {
  onUserLoginPush: "onUserLoginPush",
  eventPush: "eventPush",
  profilePush: "profilePush",
  splTrackEvent: "splTrackEvent",
  getPlatform: "getPlatform",
};

const handleEventTracking = (event: EventTrackingProps) => {
  const { eventName, eventType, eventProps, action } = event;
  if (eventProps) {
    eventProps["page_name"] = "Profile Performance";
  }

  switch (eventType) {
    case cleverTapEventTypes.eventPush:
      trackClevertapEvents.eventPush(eventName, eventProps);
      break;
    case cleverTapEventTypes.onUserLoginPush:
      trackClevertapEvents.onuserLoginPush(eventProps);
      break;
    case cleverTapEventTypes.profilePush:
      trackClevertapEvents.profilePush(eventProps);
      break;
    case cleverTapEventTypes.splTrackEvent:
      trackClevertapEvents.splTrackEvent(action, eventName, eventProps);
      break;
    default:
      break;
  }
};

const ProfilePerformancePage = ({ hostname }: { hostname: string }) => {
  const isMobile = useMobile();
  const [toast, setToast] = useState<ToastType>({
    show: false,
    type: "general",
    message: "",
  });

  const showToast = useCallback((val: ToastType) => {
    setToast(val);
  }, []);

  const contextValue = useMemo(
    () => ({
      translation,
      isMobile,
      showToast,
      handleEventTracking,
      hostname,
    }),
    [isMobile, hostname],
  );

  return (
    <>
      <style>{noScrollbarStyle}</style>
      <ProfileContext.Provider value={contextValue}>
        <RecruiterViewContextWrapper>
          <Toast
            position="top-center"
            showToast={toast.show}
            text={toast.message}
            type={toast.type}
            width={isMobile ? "75" : "25"}
            timer={3000}
            onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          />

          <div className="flex flex-col gap-4 md:!gap-6">
            <div className="flex flex-col gap-[6px] px-4 md:px-0 md:pt-0">
              <h1 className="m-0 text-base font-bold leading-6 text-[#17142A] md:text-xl md:leading-[30px]">
                Profile Performance
              </h1>
              <p className="m-0 text-sm leading-5 text-[#59566C]">
                Summary of how your profile performed in searches by the
                recruiter
              </p>
            </div>

            <div
              className={`box-border grid grid-cols-1 gap-5 px-4 pb-6 md:!gap-6 md:px-0 ${
                isMobile ? "" : "md:grid-cols-[328px,1fr]"
              }`}
            >
              <div className="flex flex-col">
                <ActivityLevel />
                <ProfileScore
                  additionalClassName="mt-5 md:!mt-4"
                  type="performance"
                />
              </div>

              <div className="flex flex-col gap-5 md:!gap-4">
                <SearchAppearances />
                <RecruiterActions />
                {isMobile ? (
                  <ExternalAd
                    adName="BB-Desktop-Profile-Score"
                    adSize={[300, 250]}
                    isBBSlot={true}
                    isLoggedIn={true}
                    adPageName="profilePage"
                  />
                ) : null}
              </div>
            </div>
          </div>
        </RecruiterViewContextWrapper>
      </ProfileContext.Provider>
    </>
  );
};

export default memo(ProfilePerformancePage);
